import { CheckCircle } from "@phosphor-icons/react";
import { Reveal, SectionHeading } from "@/components/site/Primitives";
import { SERVICES, WHO_WE_HELP, WHY_CHOOSE } from "@/data/content";

export const About = () => (
  <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-cream">
    <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <SectionHeading overline="Who We Are" title="About JP Accounts & Tax Consultants" testId="about" />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
          <p>
            JP Accounts & Tax Consultants is an Ahmedabad-based accounting and taxation firm committed to helping
            individuals, professionals, startups, and businesses manage their financial compliance with confidence.
          </p>
          <p>
            We believe accounting should be accurate, transparent, and hassle-free. Our team combines practical
            experience with modern accounting tools to provide reliable bookkeeping, taxation, and financial reporting services.
          </p>
          <p>
            Whether you're a salaried employee filing your first Income Tax Return or a business owner looking for
            complete accounting support, our goal is to provide practical solutions tailored to your needs.
          </p>
          <p className="text-navy font-medium">
            We focus on building long-term client relationships based on trust, professionalism, and timely service.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-white">
    <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <SectionHeading center overline="What We Do" title="Our Services"
          subtitle="End-to-end accounting and taxation support, delivered accurately and on time." testId="services" />
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.title} delay={(i % 3) * 0.1}>
              <div data-testid={`service-card-${i}`}
                className="card-glow group h-full bg-white border border-slate-200 rounded-2xl p-8 transition-all duration-500 hover:shadow-2xl hover:shadow-navy/10 hover:-translate-y-2 hover:border-gold/40">
                <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-white group-hover:rotate-6 group-hover:scale-110">
                  <Icon size={30} weight="duotone" />
                </div>
                <h3 className="mt-6 font-display text-xl md:text-2xl font-semibold text-navy">{s.title}</h3>
                <ul className="mt-5 space-y-2.5">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-slate-600">
                      <CheckCircle size={18} weight="fill" className="text-gold mt-0.5 shrink-0" />
                      <span className="text-sm md:text-base">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const WhoWeHelp = () => (
  <section id="who-we-help" data-testid="who-we-help-section" className="py-24 md:py-32 bg-navy relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.04]"
      style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "26px 26px" }} />
    <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-gold">Built For You</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-white">Who We Help</h2>
          <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
            From first-time filers to growing companies — if you're on this list, this practice is built for your needs.
          </p>
        </div>
      </Reveal>
      <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-6">
        {WHO_WE_HELP.map((w, i) => {
          const Icon = w.icon;
          return (
            <Reveal key={w.label} delay={(i % 3) * 0.1}>
              <div data-testid={`who-we-help-${i}`}
                className="card-glow group h-full flex flex-col items-center text-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-8 transition-all duration-500 hover:bg-white/10 hover:border-gold/40 hover:-translate-y-1.5">
                <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center text-gold transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                  <Icon size={32} weight="duotone" />
                </div>
                <span className="text-white font-medium text-sm md:text-base">{w.label}</span>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const WhyChooseUs = () => (
  <section id="why-us" data-testid="why-us-section" className="py-24 md:py-32 bg-cream">
    <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <SectionHeading center overline="Our Promise" title="Why Choose Us"
          subtitle="Six reasons individuals and businesses across India trust us with their finances." testId="why-us" />
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200">
        {WHY_CHOOSE.map((c, i) => {
          const Icon = c.icon;
          return (
            <Reveal key={c.title} delay={(i % 3) * 0.08} className="bg-white">
              <div data-testid={`why-choose-${i}`} className="group h-full p-8 md:p-10 transition-colors duration-300 hover:bg-gold/[0.05]">
                <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-gold transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6">
                  <Icon size={26} weight="duotone" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy">{c.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
